import { useTranslation } from 'next-i18next';
import type { WebsiteData } from '@/api/ai/website';
import { WebsiteInfo } from './components/WebsiteInfo';
export interface WebsitePreviewCardProps {
  websiteData: WebsiteData;
}
const FALLBACK_THUMBNAIL = '/assets/ogimage.png';

/**
 * 외부 페이지에서 가져온 URL을 그대로 `<img src>` / `<a href>`에 넣기 전,
 * http(s)인지 한 번 더 확인한다. backend가 이미 스킴을 거르지만, 향후
 * 응답 모양이 바뀌어도 FE에서 javascript:/data:/file: 같은 위험 스킴이
 * 노출되지 않도록 defense-in-depth로 둔다.
 */
const isSafeHttpUrl = (value: string | undefined): value is string => {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Website 미리보기 카드 컴포넌트
 */
export const WebsitePreviewCard = ({
  websiteData
}: WebsitePreviewCardProps) => {
  const {
    t
  } = useTranslation(['build']);
  const safeThumbnail = isSafeHttpUrl(websiteData.thumbnailUrl) ? websiteData.thumbnailUrl : FALLBACK_THUMBNAIL;
  const safeHref = isSafeHttpUrl(websiteData.url) ? websiteData.url : undefined;
  return <a href={safeHref} target="_blank" rel="noopener noreferrer" aria-label={t('build:build.website.previewCard.openInNewWindowAria', {
    title: websiteData.title
  })} className="flex h-[316px] w-full max-w-[472px] flex-col gap-sm overflow-hidden rounded-sm border border-alternative bg-white pb-md shadow-xs transition-shadow hover:shadow-md" data-sentry-component="WebsitePreviewCard" data-sentry-source-file="WebsitePreviewCard.tsx">
      {/* 썸네일 영역 — 외부 origin으로의 referrer leak을 막기 위해 no-referrer. */}
      <img src={safeThumbnail} alt={websiteData.title} loading="lazy" referrerPolicy="no-referrer" className="min-h-0 flex-1 bg-gray-50 object-cover" />

      {/* 웹사이트 정보 */}
      <div className="px-md">
        <WebsiteInfo websiteData={websiteData} data-sentry-element="WebsiteInfo" data-sentry-source-file="WebsitePreviewCard.tsx" />
      </div>
    </a>;
};